import { $Values } from "utility-types";
import * as Sentry from "@sentry/nextjs";
import { piiSanitize } from "./piiSanitize";
import { mapObject } from "../object";
import { flattenObject } from "../flatten";
import { TraceProvider } from "./traceProvider";

export const LEVEL = {
  info: "info",
  debug: "debug",
  warning: "warning",
  error: "error",
} as const;
type LEVEL_TYPE = $Values<typeof LEVEL>;

export interface ILogOutput {
  level: LEVEL_TYPE;
  traceId: string | null;
  timestamp: Date;
  message: string;
  platform: "server" | "web";
  metadata: unknown;
  // requestDetail: RequestDetail | null;
  requestId?: string;
  stack?: unknown;
}

const LogFn = {
  info: console.info,
  debug: console.log,
  warning: console.warn,
  error: console.error,
} as const satisfies Record<LEVEL_TYPE, typeof console.log>;

const getPlatform = () => {
  let platform: "server" | "web" = "server";
  if (typeof window !== "undefined") {
    platform = "web";
  }
  return platform;
};

const createLogger =
  (level: LEVEL_TYPE, traceProvider: TraceProvider) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (message: string, metadata: any = {}) => {
    function runner() {
      metadata = piiSanitize(metadata);
      // if platform web, and production, don't output anything
      const platform = getPlatform();
      const traceId = traceProvider.getTraceId();
      if (process.env.NEXT_PUBLIC_ENV !== "dev") {
        // Handle primitive metadata (string, number, etc.) by wrapping in object
        const normalizedMetadata =
          typeof metadata === "object" && metadata !== null
            ? metadata
            : { value: metadata };

        const mappedMetadata = mapObject(normalizedMetadata, (m) => {
          if (typeof m === "string" || typeof m === "number") return m;
          try {
            const stringified = JSON.stringify(m);
            return stringified;
          } catch {
            return "";
          }
        });
        if (level === LEVEL.info) {
          Sentry.logger.info(message, flattenObject(mappedMetadata));
        } else {
          Sentry.logger.debug(message, flattenObject(mappedMetadata));
        }
      }
      if (platform === "web") {
        if (process.env.NEXT_PUBLIC_ENV !== "dev") {
          return;
        }
        console.group(`[LOG][${level}] ${message}`);
        console.log("time", new Date());
        console.log("platform", platform);
        console.log("level", level);
        console.log("traceId", traceId);
        console.log("message", message);
        console.log("metadata", metadata);
        console.groupEnd();
        return;
      }
      const logOutput: ILogOutput = {
        traceId,
        requestId: "",
        message,
        metadata,
        level,
        platform,
        timestamp: new Date(),
        // requestDetail: requestId ? getRequestDetail(requestId) : null,
      };

      LogFn[level](JSON.stringify(logOutput));

      // Ship log to external service if provider supports it
      if (traceProvider.shipLog) {
        traceProvider.shipLog(logOutput);
      }
    }
    // defer to after callstack
    setTimeout(runner, 0);
  };

const createErrorLogger =
  (traceProvider: TraceProvider) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (error: Error | string, metadata: any = {}) => {
    function runner() {
      const getMessage = () => {
        if (typeof error === "string") {
          return error;
        }
        return error.message;
      };

      const getStack = () => {
        if (typeof error === "string") {
          return new Error(error).stack;
        }

        return error.stack;
      };
      metadata = piiSanitize(metadata);
      const traceId = traceProvider.getTraceId();
      // if platform web, and production, don't output anything
      // dont log sentry in dev
      if (process.env.NEXT_PUBLIC_ENV !== "dev") {
        // Handle primitive metadata (string, number, etc.) by wrapping in object
        const normalizedMetadata =
          typeof metadata === "object" && metadata !== null
            ? metadata
            : { value: metadata };

        const mappedMetadata = mapObject(normalizedMetadata, (m) => {
          if (typeof m === "string" || typeof m === "number") return m;
          try {
            const stringified = JSON.stringify(m);
            return stringified;
          } catch {
            return "";
          }
        });
        Sentry.logger.error(getMessage(), mappedMetadata);
      }
      const platform = getPlatform();
      if (platform === "web") {
        if (process.env.NEXT_PUBLIC_ENV !== "dev") {
          // remove ALL LOG CONTEXT ON NON-DEV CLIENT
          return;
        }

        console.error(error);
        console.group(`[LOG][error] ${getMessage()}`);
        console.log("traceId", traceId);
        console.log("time", new Date());
        console.log("platform", platform);
        console.log("level", "error");
        console.log("message", getMessage());
        console.log("stack", getStack());
        console.log("metadata", metadata);
        console.log(
          "requestDetail",
          // requestId ? getRequestDetail(requestId) : null,
        );
        console.groupEnd();
        return;
      }

      const logOutput: ILogOutput = {
        requestId: "",
        traceId,
        message: getMessage(),
        metadata,
        stack: getStack(),
        platform,
        level: "error",
        // requestDetail: requestId ? getRequestDetail(requestId) : null,
        timestamp: new Date(),
      };

      console.error(JSON.stringify(logOutput));

      // Ship log to external service if provider supports it
      if (traceProvider.shipLog) {
        traceProvider.shipLog(logOutput);
      }
    }

    setTimeout(runner, 0);
  };

export { createLogger, createErrorLogger };
